import { PlayerEvent } from 'bitmovin-player';
import { throttle } from 'lodash';
import { useCallback, useRef } from 'react';
import playerService from '../../../helpers/bitmovinPlayer';

// TODO: move to hooks/
const useCustomSeekbar = () => {
    const seekbarElementID = '#custom-seekbar-wrapper';
    const isScrubbingRef = useRef(false);

    const updateScrubbingState = useCallback((value: boolean) => {
        isScrubbingRef.current = value;
    }, []);

    /**
     * Custom seekbar for the player with thumbnail preview functionality. Ads duration is subtracted from main asset time
     * to ensure accurate seeking and progress display for AVOD content.
     *
     * Initializes seekbar elements and attaches event listeners for:
     * - Displaying thumbnails on hover
     * - Preview thumbnail updates on mouse movement
     * - Seeking to clicked position
     * - Updating playback position marker as the video plays
     *
     * @param {any} player - The player instance
     */
    const setupCustomSeekbar = useCallback(
        (player) => {
            // TODO: seekbar element ID is used here and in useAvod.ts. Better to refactor it to CONSTANTS or something
            // like that
            const seekbarWrapper = document.querySelector(seekbarElementID);
            const seekbar: HTMLElement = seekbarWrapper.querySelector('.bmpui-seekbar');
            const playbackPositionElement: HTMLElement = seekbar.querySelector('.bmpui-seekbar-playbackposition');
            const playbackPositionMarker: HTMLElement = seekbar.querySelector('.bmpui-seekbar-playbackposition-marker');
            const seekbarBackdrop: HTMLElement = seekbar.querySelector('.bmpui-seekbar-backdrop');

            const getPointerPosition = (e: MouseEvent): string => {
                const rect = seekbarBackdrop.getBoundingClientRect();
                const offsetX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
                return `${offsetX}px`;
            };

            const updatePlaybackPositionUI = (e: MouseEvent) => {
                if (!isScrubbingRef.current) return;

                const pointerPosition = getPointerPosition(e);
                playbackPositionElement.style.width = pointerPosition;
                const playbackPositionElementWidth = playbackPositionElement.clientWidth + 'px';

                if (playbackPositionMarker) {
                    playbackPositionMarker.style.transform = `translateX(${playbackPositionElementWidth})`;
                }
            };

            // TODO: instead of getting ad duration from playerService, should it be passed as a parameter to this function?
            const adsTotalDuration = playerService.getAdsData()?.duration / 1000 || 0;

            if (!seekbar || !seekbarBackdrop || !playbackPositionElement || !playbackPositionMarker) {
                return;
            }

            const thumbnailContainer: HTMLElement = seekbarWrapper.querySelector('.bmpui-ui-seekbar-label');
            const thumbnail: HTMLElement = seekbarWrapper.querySelector('.bmpui-seekbar-thumbnail');

            if (thumbnail) {
                thumbnail.style.paddingBottom = '56.25%';
                thumbnail.style.backgroundSize = '1000% 100%';
            }

            if (thumbnailContainer) {
                thumbnailContainer.classList.add('bmpui-hidden');
                seekbar.addEventListener('mouseenter', () => thumbnailContainer.classList.remove('bmpui-hidden'));
                seekbar.addEventListener('mouseleave', () => thumbnailContainer.classList.add('bmpui-hidden'));
            }

            const onMouseDown = () => {
                updateScrubbingState(true);
            };

            const onMouseUp = () => {
                if (!isScrubbingRef.current) return;
                updateScrubbingState(false);
            };

            const onMouseMove = (e: MouseEvent) => {
                // TODO: throttle?
                const rect = seekbarBackdrop.getBoundingClientRect();
                const offsetX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
                const pointerPosition = getPointerPosition(e);
                const percentage = (offsetX / rect.width) * 100;

                const pointerPositionInSeconds = (percentage / 100) * (player.getDuration() - adsTotalDuration);
                const img = player.getThumbnail(pointerPositionInSeconds + adsTotalDuration);

                if (thumbnail) {
                    thumbnailContainer.style.left = pointerPosition;
                }

                if (img && img.url) {
                    thumbnail.style.backgroundImage = `url(${img.url})`;
                }

                updatePlaybackPositionUI(e);
            };

            seekbar.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);

            // mapping where the user clicks on the custom seekbar to the corresponding playback time, while
            // accounting for ad time.
            seekbar.addEventListener('click', (e: MouseEvent) => {
                const rect = seekbarBackdrop.getBoundingClientRect();
                const offsetX = e.clientX - rect.left;
                const percentage = (offsetX / rect.width) * 100;
                const clickPosition = (percentage / 100) * (player.getDuration() - adsTotalDuration);

                player.seek(clickPosition + adsTotalDuration);
            });

            // updating the playback position and marker position as the video plays, while accounting for ad time
            player.on(PlayerEvent.TimeChanged, () => {
                let playbackPositionElementWidth = '0px';

                // Subtract ad duration from current time, ensuring it doesn't go below 0
                const currentTime =
                    player.getCurrentTime() - adsTotalDuration > 0 ? player.getCurrentTime() - adsTotalDuration : 0;
                const duration = player.getDuration() - adsTotalDuration;
                const percentageWatched = (currentTime / duration) * 100;

                if (playbackPositionElement) {
                    playbackPositionElement.style.width = `${percentageWatched}%`;
                    playbackPositionElementWidth = playbackPositionElement.clientWidth + 'px';

                    if (playbackPositionMarker) {
                        playbackPositionMarker.style.transform = `translateX(${playbackPositionElementWidth})`;
                    }
                }
            });
            // For cleanup:
            player.on(PlayerEvent.Destroy, () => {
                seekbar.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('mouseup', onMouseUp);
                document.removeEventListener('mousemove', onMouseMove);
            });
        },
        [updateScrubbingState]
    );

    return { setupCustomSeekbar };
};

export default useCustomSeekbar;
